import { combineLatest, Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { formatToday } from '../../../utils/date';
import { queryHashGenerator } from '../../../utils/queryHash';
import {
  EntityType,
  IAggregateWithMetric,
  IMetric,
  ISelectableMetric,
  ISelectableTag,
} from '../../types';
import { aggregatesProvider, IAggregates } from '../aggregatesProvider';
import {
  comparisonReportSettingsProvider,
  IComparisonReportSettings,
  IComparisonReportSettingsState,
} from '../settings/reports/comparisonReportSettingsProvider';
import { ISettingsTemplate } from '../settings/reports/types';
import { determinePopulation } from '../utils/determinePopulation';
import {
  convertTimeframeToLocalTime,
  formatTimeframe,
} from '../utils/formatTimeframe';
import { getSessionRangeTimestamps } from '../utils/getSessionRangeTimestamps';
import { isValidComparisonPopulation } from '../utils/isValidComparisonPopulation';
import { IReportState } from './types';

interface IDataSetResult {
  aggregate: IAggregateWithMetric;
  timeframe: string;
  percentage: number;
  count: number;
}
export interface IComparisonResult {
  metric: IMetric;
  dataSetA: IDataSetResult;
  dataSetB: IDataSetResult;
  change: number;
  percentDifference: number;
  isValid: boolean;
}

export interface IComparisonReportState
  extends IReportState,
    IComparisonReportSettingsState {
  settings: ISettingsTemplate<IComparisonReportSettings>;
  data: IComparisonResult[];
}

/**
 * @description pipeline that observes comparison data requested based on the comparison report settings
 * 	and then determines and emits the state of the report
 * @param entityType
 * @param entityId
 * @returns
 */
export const comparisonReportProvider = (
  entityType: EntityType,
  entityId: string
): Observable<IComparisonReportState> => {
  return comparisonReportSettingsProvider().pipe(
    /// observe the aggregates as defined in the settings
    switchMap(
      (
        settingsState: IComparisonReportSettingsState
      ): Observable<
        [IComparisonReportSettingsState, IAggregates, IAggregates]
      > => {
        const settingsValidation = validateSettings(settingsState);
        if (settingsValidation.isValid) {
          const queryHashA = queryHashGenerator(
            settingsState.selected.settings.testType?.value,
            settingsState.selected.settings.target?.tags?.map(
              (t: ISelectableTag): string => t.value
            )
          );
          const queryHashB = queryHashGenerator(
            settingsState.selected.settings.testType.value,
            settingsState?.selected.settings.population?.tags?.map(
              (t: ISelectableTag): string => t.value
            )
          );
          const dataSetA$ = aggregatesProvider(
            entityId,
            entityType,
            queryHashA,
            settingsState.targetTimeframe?.dateType,
            settingsState.targetTimeframe?.dateRange
          );
          const [populationEntityType, populationEntityId] =
            determinePopulation(settingsState.comparison?.entity, {
              entityId,
              entityType,
            });
          const dataSetB$ = aggregatesProvider(
            populationEntityId,
            populationEntityType as EntityType,
            queryHashB,
            settingsState.comparison.dateRangeType,
            settingsState.comparison.dateRangeValues
          );
          return combineLatest([dataSetA$, dataSetB$]).pipe(
            map(
              (
                results: [IAggregates, IAggregates]
              ): [IComparisonReportSettingsState, IAggregates, IAggregates] => {
                const [dataSetA, dataSetB] = results;
                return [settingsState, dataSetA, dataSetB];
              }
            )
          );
        } else {
          return of([settingsState, null, null]);
        }
      }
    ),
    /// map the aggregates to a state
    map(
      (
        result: [IComparisonReportSettingsState, IAggregates, IAggregates]
      ): IComparisonReportState => {
        const [settingsState, dataSetA, dataSetB] = result;

        const settingsValidation = validateSettings(settingsState);
        if (settingsValidation.isValid) {
          const results = makeComparsionResults(
            settingsState,
            dataSetA,
            dataSetB
          );
          const dataValidation = validateData(results);
          const localComparisonTimeframe = convertTimeframeToLocalTime({
            dateType: settingsState?.comparison?.dateRangeType,
            dateRange: settingsState?.comparison?.dateRangeValues,
          });
          return {
            ...dataValidation,
            ...settingsState,
            targetTimeframe: convertTimeframeToLocalTime(
              settingsState.targetTimeframe
            ),
            comparison: {
              entity: settingsState?.comparison?.entity,
              zScorePreference: settingsState?.comparison?.zScorePreference,
              dateRangeType: localComparisonTimeframe?.dateType,
              dateRangeValues: localComparisonTimeframe?.dateRange,
            },
            settings: settingsState.selected,
            data: results,
            timeframe: formatToday(),
          };
        } else {
          return {
            ...settingsValidation,
            ...settingsState,
            settings: settingsState.selected,
            data: [],
            timeframe: formatToday(),
          };
        }
      }
    )
  );
};

/**
 * @description generates the individual comparison result for each metric defined in the report settings
 * @param settingsState
 * @param aggregates
 * @returns
 */
const makeComparsionResults = (
  settingsState: IComparisonReportSettingsState,
  dataSetA: IAggregates,
  dataSetB: IAggregates
): IComparisonResult[] => {
  const settings = settingsState?.selected?.settings;
  return settings.metrics[settings.testType?.value]?.map(
    (metric: ISelectableMetric): IComparisonResult => {
      const aggregateA = dataSetA?.combined?.data[metric.value];
      const aggregateB = dataSetB?.combined?.data[metric.value];
      if (aggregateA?.count > 0 && aggregateB?.count > 0) {
        const { start: startA, end: endA } = getSessionRangeTimestamps(
          dataSetA.sessions
        );
        const { start: startB, end: endB } = getSessionRangeTimestamps(
          dataSetB.sessions
        );
        const meanA = Math.abs(aggregateA?.mean ?? 0);
        const meanB = Math.abs(aggregateB?.mean ?? 0);
        const total = meanA + meanB;
        const change = meanA - meanB;

        return {
          metric: metric.entity,
          dataSetA: {
            aggregate: aggregateA,
            percentage: ((meanA ?? 0) / total) * 100,
            timeframe: formatTimeframe(startA, endA),
            count: aggregateA?.count ?? 0,
          },
          dataSetB: {
            aggregate: aggregateB,
            percentage: ((meanB ?? 0) / total) * 100,
            timeframe: formatTimeframe(startB, endB),
            count: aggregateB?.count ?? 0,
          },
          change,
          percentDifference: (change / (total / 2)) * 100,
          isValid: true,
        };
      } else {
        return {
          metric: metric.entity,
          dataSetA: {
            aggregate: aggregateA,
            percentage: null,
            timeframe: null,
            count: aggregateA?.count ?? 0,
          },
          dataSetB: {
            aggregate: aggregateB,
            percentage: null,
            timeframe: null,
            count: aggregateB?.count ?? 0,
          },
          change: null,
          percentDifference: null,
          isValid: false,
        };
      }
    }
  );
};

interface IValidationResult {
  isValid: boolean;
  errorCode?: string;
}

/**
 * @description validates the user settings for the report
 * @param settingsState
 * @returns
 */
const validateSettings = (
  settingsState: IComparisonReportSettingsState
): IValidationResult => {
  /// check to make sure test type is defined
  if (!settingsState?.selected) {
    return {
      isValid: false,
      errorCode: 'missing-template',
    };
  }
  const settings = settingsState?.selected?.settings;
  if (!settings?.testType?.value) {
    return {
      isValid: false,
      errorCode: 'missing-test-type',
    };
  }

  /// check to make sure metrics are defined
  if (!(settings?.metrics?.[settings?.testType.value]?.length > 0)) {
    return {
      isValid: false,
      errorCode: 'missing-metrics',
    };
  }
  if (!isValidComparisonPopulation(settingsState?.comparison, true)) {
    return {
      isValid: false,
      errorCode: 'missing-comparison-population',
    };
  }

  return { isValid: true };
};

/**
 * @description validates results (each data set has at least one metric that has results)
 * @param results
 * @returns
 */
const validateData = (results: IComparisonResult[]): IValidationResult => {
  let dataSetAValid: boolean;
  let dataSetBValid: boolean;
  for (const result of results) {
    if (result.dataSetA?.count > 0) {
      if (!dataSetAValid) {
        dataSetAValid = true;
      }
    }
    if (result.dataSetB?.count > 0) {
      if (!dataSetBValid) {
        dataSetBValid = true;
      }
    }
  }
  if (dataSetAValid && dataSetBValid) {
    return { isValid: true };
  }
  if (!dataSetAValid) {
    return {
      isValid: false,
      errorCode: 'dataset-a-invalid',
    };
  }
  if (!dataSetBValid) {
    return {
      isValid: false,
      errorCode: 'dataset-b-invalid',
    };
  }
};
